import { authApi, siteApi } from "@api";
import MemberSettingsRenderer from "@components/utils/MemberSettingsRenderer";
import { DEFAULT_ERROR_MESSAGE } from "@constants/misc";
import { ValidatorSchema } from "@constants/validators";
import { zodResolver } from "@hookform/resolvers/zod";
import useCaptureFormValidationErrors from "@hooks/useCaptureFormValidationErrors";
import useContactLinks from "@hooks/useContactLinks";
import { useMemberSettings } from "@hooks/useMemberSettings";
import usePostLoginInfo from "@hooks/usePostLoginInfo";
import { AppDispatch } from "@stores";
import authSlice from "@stores/auth/slices.ts";
import { buildPersonalInfoRequest } from "@utils/personal-info";
import {
  APIErrorCodes,
  Button,
  CreateFundingProfileRequest,
  FundingProfileType,
  MemberSettingNameMode,
  ProfileResponse,
  PropsWithClassName,
  TextField,
  cn,
  useHandleApiResponse,
} from "kz-ui-sdk";
import { useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { z as zod } from "zod";
import BankSelect from "../BankSelect";
import RHFController from "../RHFController";

interface UpdateBankAccountFormProps extends PropsWithClassName {
  onUpdateSuccess?: (response?: ProfileResponse) => void;
  mode?: "update" | "add";
  defaultNameMode?: MemberSettingNameMode;
}

const bankFormSchema = zod.object({
  accountNo: ValidatorSchema.payment.bank.accountNo,
  firstName: ValidatorSchema.firstName,
  lastName: ValidatorSchema.lastName,
  accountName: ValidatorSchema.accountName,
  bankKey: ValidatorSchema.payment.bank.bankKey,
});

type Values = zod.infer<typeof bankFormSchema>;

const defaultValue = { accountNo: "", firstName: "", lastName: "", bankKey: "", accountName: "" } satisfies Values;

const FormUpdateBankAccount = ({ onUpdateSuccess, className, mode, defaultNameMode }: UpdateBankAccountFormProps) => {
  const { handleApiResponse } = useHandleApiResponse({
    toast,
  });
  const { t } = useTranslation();
  const [addBankAcc, { isLoading: isAddingBankAcc }] = siteApi.useCreateFundingProfileMutation();
  const [updatePersonalInfo, { isLoading: isUpdatingPersonalInfo }] = siteApi.useUpdatePersonalInfoMutation();
  const { nameMode: mbrSettingNameMode } = useMemberSettings();
  const { isLegalNameFulfilled } = usePostLoginInfo();

  const nameMode = defaultNameMode ?? mbrSettingNameMode ?? MemberSettingNameMode.FirstLast;

  const dispatch = useDispatch<AppDispatch>();

  const { openContact } = useContactLinks();

  const handleOpenContactLink = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      openContact();
    },
    [openContact],
  );

  const formSchema = useMemo(() => {
    // If legal name is fulfilled, omit account name, first name, and last name
    if (isLegalNameFulfilled) {
      return bankFormSchema.omit({ accountName: true, firstName: true, lastName: true });
    }
    switch (nameMode) {
      case MemberSettingNameMode.FirstLast:
        return bankFormSchema.omit({ accountName: true });
      case MemberSettingNameMode.FullInFirst:
        return bankFormSchema.omit({ firstName: true, lastName: true });
      default:
        return bankFormSchema;
    }
  }, [nameMode, isLegalNameFulfilled]);

  const {
    handleSubmit,
    control,
    watch,
    setError,
    setValue,
    formState: { errors },
  } = useForm<Values>({
    mode: "onChange",
    defaultValues: defaultValue,
    resolver: zodResolver(formSchema),
  });

  // Capture form validation errors
  useCaptureFormValidationErrors(errors, {
    watch,
    feature: "bank-account-update",
  });

  const updateName = useCallback(
    async (v: Values) => {
      const response = await updatePersonalInfo(buildPersonalInfoRequest(v, nameMode));
      handleApiResponse(response);
      if (response.error) {
        return false;
      }
      return true;
    },
    [nameMode, handleApiResponse, updatePersonalInfo],
  );

  const onSubmit = useCallback(
    async (v: Values) => {
      const requestBankInfo: CreateFundingProfileRequest = {
        key: v.bankKey,
        accNo: v.accountNo.trim(),
        type: FundingProfileType.Bank,
        isPrimary: true,
      };

      if (!isLegalNameFulfilled) {
        const isUpdateNameSuccess = await updateName(v);
        if (!isUpdateNameSuccess) {
          return;
        }
      }

      const result = await addBankAcc(requestBankInfo);
      handleApiResponse(result, {
        successMessage: t("Updated bank account successfully!"),
        toastError: false,
        onSuccess: async () => {
          // refresh self
          const result = await dispatch(
            authApi.endpoints.getProfile.initiate(undefined, {
              forceRefetch: true,
            }),
          );
          if (result.data) {
            dispatch(authSlice.actions.updateProfile(result.data));
          }

          onUpdateSuccess?.(result.data);
        },
        onError: (error) => {
          const errorMessage = error.data?.error?.message;
          switch (errorMessage) {
            case APIErrorCodes.FundingProfile.LinkedToAnotherMember:
              setError("accountNo", { message: "account number is not valid" });
              break;
            default:
              toast.error(t(errorMessage ?? DEFAULT_ERROR_MESSAGE));
              return;
          }
        },
      });
    },
    [addBankAcc, dispatch, handleApiResponse, onUpdateSuccess, t, setError, isLegalNameFulfilled, updateName],
  );

  const isLoading = isAddingBankAcc || isUpdatingPersonalInfo;

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <RHFController
        control={control}
        name="bankKey"
      >
        <BankSelect
          disabled={isLoading}
          className="mb-1"
        />
      </RHFController>

      {!isLegalNameFulfilled && (
        <>
          {/* FIRST NAME & LAST NAME */}
          <MemberSettingsRenderer
            feature="nameMode"
            match={MemberSettingNameMode.FirstLast}
          >
            <RHFController
              control={control}
              name="firstName"
              onBlur={(v) => {
                setValue("firstName", v.trim());
              }}
            >
              <TextField
                classes={{
                  "input&": "w-full truncate",
                }}
                placeholder={t("First name")}
                disabled={isLoading}
                size={"lg"}
              />
            </RHFController>
            <RHFController
              control={control}
              name="lastName"
              onBlur={(v) => {
                setValue("lastName", v.trim());
              }}
            >
              <TextField
                classes={{
                  "input&": "w-full truncate",
                }}
                placeholder={t("Last name")}
                disabled={isLoading}
                size={"lg"}
              />
            </RHFController>
          </MemberSettingsRenderer>

          {/* ACCOUNT NAME */}
          <MemberSettingsRenderer
            feature="nameMode"
            match={MemberSettingNameMode.FullInFirst}
          >
            <label className="flex gap-2">
              <RHFController
                control={control}
                name="accountName"
                onBlur={(v) => {
                  setValue("accountName", v.trim());
                }}
              >
                <TextField
                  classes={{
                    "input&": "w-full truncate ",
                  }}
                  placeholder={t("Full name")}
                  disabled={isLoading}
                  size={"lg"}
                />
              </RHFController>
            </label>
          </MemberSettingsRenderer>
        </>
      )}

      <label className="flex flex-col gap-2">
        <RHFController
          control={control}
          name="accountNo"
          onBlur={(v) => {
            setValue("accountNo", v.trim());
          }}
        >
          <TextField
            classes={{
              "input&": "w-full truncate",
            }}
            helperTextClasses={{
              className: "h-0 overflow-hidden",
            }}
            placeholder={t("Account number")}
            disabled={isLoading}
            size={"lg"}
          />
        </RHFController>
        {errors.accountNo && (
          <div
            className={cn("-mt-1.5 max-h-0 text-right text-xs leading-4 text-error-base transition-[max-height]", {
              "max-h-5": !!errors.accountNo.message,
            })}
          >
            {errors.accountNo.message === "account number is not valid" && (
              <span className="h-5 text-error-base">
                {t("account number is not valid")}{" "}
                <button
                  onClick={handleOpenContactLink}
                  className="underline"
                >
                  {t("contact cs")}
                </button>
              </span>
            )}
            {errors.accountNo.message !== "account number is not valid" && (
              <span className="h-5 text-error-base">{t(errors.accountNo.message || "")}</span>
            )}
          </div>
        )}
      </label>
      <Button
        onClick={handleSubmit(onSubmit)}
        loading={isLoading}
        size={"lg"}
        className="mt-3"
      >
        {mode === "update" ? t("Update") : t("finish & login")}
      </Button>
    </div>
  );
};

export default FormUpdateBankAccount;
